import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './style.less';

const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.pdf', '.doc', '.docx', '.dwg'];
const ACCEPT_STRING = ACCEPTED_EXTENSIONS.join(',');

const ICON_MAP = {
  jpg: 'fa-file-image',
  jpeg: 'fa-file-image',
  png: 'fa-file-image',
  webp: 'fa-file-image',
  pdf: 'fa-file-pdf',
  doc: 'fa-file-word',
  docx: 'fa-file-word',
  dwg: 'fa-file-alt',
};

const getFileExtension = (fileName) => {
  return fileName.split('.').pop().toLowerCase();
};

const getFileIcon = (fileName) => {
  const ext = getFileExtension(fileName);
  return ICON_MAP[ext] || 'fa-file-alt';
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isValidExtension = (fileName) => {
  const ext = '.' + getFileExtension(fileName);
  return ACCEPTED_EXTENSIONS.includes(ext);
};

export const FileUpload = ({
  files = [],
  onFilesChange,
  maxFiles = 5,
  maxSizeBytes = 5 * 1024 * 1024,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState('');
  const inputRef = useRef(null);

  const validateFiles = useCallback(
    (newFiles) => {
      const totalCount = files.length + newFiles.length;
      if (totalCount > maxFiles) {
        setValidationError(t('contact.errors.tooManyFiles'));
        return null;
      }

      const validFiles = [];
      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];

        if (!isValidExtension(file.name)) {
          setValidationError(t('contact.errors.invalidFileType'));
          return null;
        }

        if (file.size > maxSizeBytes) {
          setValidationError(t('contact.errors.fileTooLarge'));
          return null;
        }

        validFiles.push(file);
      }

      setValidationError('');
      return validFiles;
    },
    [files.length, maxFiles, maxSizeBytes, t]
  );

  const handleFiles = useCallback(
    (fileList) => {
      const newFiles = Array.from(fileList);
      const validated = validateFiles(newFiles);
      if (validated) {
        onFilesChange([...files, ...validated]);
      }
    },
    [files, onFilesChange, validateFiles]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e) => {
    if (e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const openFilePicker = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openFilePicker();
    }
  };

  const handleRemove = (index) => {
    const updated = files.filter((_, i) => i !== index);
    onFilesChange(updated);
    setValidationError('');
  };

  const displayError = error || validationError;

  return (
    <div className={`file-upload${disabled ? ' file-upload--disabled' : ''}`}>
      <label className="file-upload__label">{t('contact.form.attachments')}</label>

      <div
        className={`file-upload__dropzone${isDragOver ? ' file-upload__dropzone--active' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFilePicker}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={t('contact.form.attachments')}
      >
        <i className="fas fa-cloud-upload-alt file-upload__dropzone-icon" />
        <span className="file-upload__dropzone-text">
          {t('contact.form.attachmentsDropzone')}
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_STRING}
        multiple
        onChange={handleInputChange}
        className="file-upload__input"
        aria-hidden="true"
        tabIndex={-1}
      />

      <p className="file-upload__hint">{t('contact.form.attachmentsHint')}</p>

      {displayError && (
        <p className="file-upload__error" role="alert" aria-live="assertive">
          {displayError}
        </p>
      )}

      {files.length > 0 && (
        <ul className="file-upload__list">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="file-upload__item">
              <i className={`fas ${getFileIcon(file.name)} file-upload__item-icon`} />
              <span className="file-upload__item-name">{file.name}</span>
              <span className="file-upload__item-size">{formatFileSize(file.size)}</span>
              <button
                type="button"
                className="file-upload__item-remove"
                onClick={() => handleRemove(index)}
                aria-label={t('contact.form.attachmentsRemove')}
              >
                <i className="fas fa-times" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
